import type { TypedReadSchema, TypedReadSchemaItem } from '@common/formEngine/typedReadSchema.types';
import type { IntoxicationCustomControlData, IntoxicationReadLabel } from '../intoxicationSchemas';
import type { OsasConsultData, OsasConsultExaminationReadKey } from './types';

export const osasConsultOtherRelevantFindingsLabels = {
    frequentMovements: 'Veel bewegingen',
    indicationOfHeartRhythmDisorder: 'Aanwijzing hartritmestoornis',
    inefficientSleepHygiene: 'Inefficiënte slaaphygiëne',
    suboptimalSleepEfficiency: 'Niet optimale slaapefficiëntie',
};

type OsasConsultReadData = OsasConsultData & IntoxicationCustomControlData & { years_packs: string[] };
export type OsasConsultReadKey = keyof OsasConsultReadData;
export type OsasConsultExaminationReadLabel =
    | 'Datum slaaponderzoek'
    | 'Gemiddelde AHI'
    | 'Gemiddelde ODI 3%'
    | 'Gemiddelde ODI 4%'
    | 'Gemiddelde saturatie %'
    | 'Laagst gemeten saturatie %'
    | 'T90 %'
    | 'Centrale SA >10'
    | 'Relevante hypoxemie'
    | 'Positieafhankelijk'
    | 'Overige relevante bevindingen'
    | 'Toelichting';

export type OsasConsultReadLabel =
    | OsasConsultExaminationReadLabel
    | IntoxicationReadLabel
    | 'Aanvullend lichamelijk onderzoek'
    | 'Aanvullende anamnese'
    | 'Aanvullende comorbiditeiten'
    | 'Aanvullende conclusie'
    | 'Advies aan de huisarts'
    | 'Andere conclusie dan OSA'
    | 'Behandeling'
    | 'Bericht aan de huisarts'
    | 'BMI categorie'
    | 'Comorbiditeiten volgens verwijzing'
    | 'CPAP instellingen'
    | 'Vervolg consult'
    | 'Vervolg slaaponderzoek'
    | 'Vervolgonderzoek na'
    | 'Vervolgconsult na'
    | 'Datum consult met de patiënt'
    | 'E-health programma´s'
    | 'Herzend Ksyos vragenlijsten na'
    | 'Huisarts op de hoogte brengen'
    | 'Mate van OSA'
    | 'Mate van ziektelast'
    | 'Obstructieve Slaapapneu (OSA)'
    | 'Overige opmerkingen'
    | 'Patiënt heeft behandelkeuze gemaakt'
    | 'Poli belt patiënt terug na'
    | 'Rijbevoegdheid besproken'
    | 'Taak voor de Ksyos Poli'
    | 'Toelichting behandeling'
    | 'Toelichting'
    | 'Toelichting rijbevoegdheid'
    | 'Doorverwijzen aan'
    | 'Roken'
    | 'Drugsgebruik'
    | 'Jaar / pakjes per dag / pack years';

export type OsasConsultReadSectionTitle =
    | 'Aanvullende specialistische bevindingen'
    | 'Beleid'
    | 'Bevindingen slaaponderzoek'
    | 'Conclusie';

export const osasConsultExaminationReadItems: TypedReadSchemaItem<
    OsasConsultExaminationReadKey,
    OsasConsultExaminationReadLabel
>[] = [
    { key: 'examinationDate', label: 'Datum slaaponderzoek', type: 'dateAsString' },
    { key: 'pAhi3Percent', label: 'Gemiddelde AHI' },
    { key: 'odi3Percent', label: 'Gemiddelde ODI 3%' },
    { key: 'odi4Percent', label: 'Gemiddelde ODI 4%' },
    { key: 'oxygenSaturationMeanValue', label: 'Gemiddelde saturatie %' },
    { key: 'oxygenSaturationMinValue', label: 'Laagst gemeten saturatie %' },
    { key: 'T90Percent', label: 'T90 %' },
    { key: 'centralSaGreaterThan10', label: 'Centrale SA >10', type: 'boolean' },
    { key: 'relevantHypoxemia', label: 'Relevante hypoxemie', type: 'boolean' },
    { key: 'remarkRelevantHypoxemia', label: 'Toelichting' },
    { key: 'positionDependent', label: 'Positieafhankelijk' },
    { key: 'remarkPositionDependent', label: 'Toelichting' },
    { key: 'otherRelevantFindings', label: 'Overige relevante bevindingen' },
];

export const osasConsultReadSchema: TypedReadSchema<
    OsasConsultReadKey,
    OsasConsultReadLabel,
    OsasConsultReadSectionTitle
>[] = [
    {
        items: [{ key: 'consultDate', label: 'Datum consult met de patiënt', type: 'dateAsString' }],
    },
    {
        items: [
            { key: 'additionalAnamnesis', label: 'Aanvullende anamnese' },
            { key: 'additionalPhysicalExamination', label: 'Aanvullend lichamelijk onderzoek' },
        ],
        sectionTitle: 'Aanvullende specialistische bevindingen',
    },
    {
        items: osasConsultExaminationReadItems,
        sectionTitle: 'Bevindingen slaaponderzoek',
    },
    {
        items: [
            { key: 'hasOsa', label: 'Obstructieve Slaapapneu (OSA)' },
            { key: 'hasOsaOtherText', label: 'Andere conclusie dan OSA' },
            { key: 'measureOfOsa', label: 'Mate van OSA' },
            { key: 'measureOfDiseaseBurden', label: 'Mate van ziektelast' },
            { key: 'bmiCategory', label: 'BMI categorie' },
            { key: 'isAlcoholUser', label: 'Alcoholconsumptie' },
            { append: 'per week', key: 'daysAWeek', label: 'Alcoholconsumptie dagen', type: 'number' },
            { append: 'per dag', key: 'glassesADay', label: 'Alcoholconsumptie glazen', type: 'number' },
            { key: 'alcoholStoppedSince', label: 'Gestopt sinds' },
            { key: 'isSmoker', label: 'Roken' },
            { key: 'years_packs', label: 'Jaar / pakjes per dag / pakjaren' },
            { key: 'smokingStoppedSince', label: 'Gestopt sinds' },
            { key: 'isSmokerRemark', label: 'Toelichting' },
            { key: 'isDrugUser', label: 'Drugsgebruik' },
            { key: 'drugUseStoppedSince', label: 'Gestopt sinds' },
            { key: 'isDrugUserRemark', label: 'Toelichting' },
            { key: 'comorbiditiesFromReferral', label: 'Comorbiditeiten volgens verwijzing' },
            { key: 'comorbidities', label: 'Aanvullende comorbiditeiten' },
            { key: 'additionalConclusionText', label: 'Aanvullende conclusie' },
        ],
        sectionTitle: 'Conclusie',
    },
    {
        items: [
            { key: 'adviceGeneralPractitionerNoOsa', label: 'Advies aan de huisarts' },
            { key: 'remarkNoOsa', label: 'Toelichting' },
            { key: 'hasTreatment', label: 'Patiënt heeft behandelkeuze gemaakt', type: 'boolean' },
            { key: 'remarkNoTreatment', label: 'Toelichting' },
            { append: 'weken', key: 'callPatientAfterWeeks', label: 'Poli belt patiënt terug na', type: 'number' },
            { key: 'treatment', label: 'Behandeling' },
            { key: 'refer', label: 'Doorverwijzen aan' },
            { key: 'treatmentFollowUp', label: 'Behandeling' },
            { key: 'cpapSetting', label: 'CPAP instellingen' },
            { key: 'cpapSettingText', label: 'CPAP instellingen' },
            { key: 'shouldHaveFollowUpConsult', label: 'Vervolg consult' },
            { key: 'shouldHaveFollowingExamination', label: 'Vervolg slaaponderzoek' },
            {
                append: 'weken',
                key: 'taskFollowingExaminationOrConsultWeeks',
                label: 'Vervolgonderzoek na',
                type: 'number',
            },

            { key: 'remarkTreatment', label: 'Toelichting behandeling' },
            { key: 'taskForPoliclinic', label: 'Taak voor de Ksyos Poli' },
            { key: 'checkedTreatments', label: 'E-health programma´s' },
            { key: 'shouldNotifyGeneralPractitioner', label: 'Huisarts op de hoogte brengen', type: 'boolean' },
            { key: 'messageToGeneralPractitioner', label: 'Bericht aan de huisarts' },
            {
                append: 'weken',
                key: 'resendQuestionnairesWeeks',
                label: 'Herzend Ksyos vragenlijsten na',
                type: 'number',
            },
            { key: 'adviceGeneralPractitionerForOsa', label: 'Advies aan de huisarts' },
            { key: 'isDrivingCompetenceDiscussed', label: 'Rijbevoegdheid besproken', type: 'boolean' },
            { key: 'remarkIsDrivingCompetenceDiscussed', label: 'Toelichting rijbevoegdheid' },
            { key: 'remarkOther', label: 'Overige opmerkingen' },
        ],
        sectionTitle: 'Beleid',
    },
];

export const adviceGeneralPractitionerNoOsaOptions = [
    'Geen behandelindicatie',
    'Behandeling in de 1e lijn',
    'Doorverwijzen naar regionaal slaapcentrum',
    'Doorverwijzen naar KNO arts',
    'Anders, namelijk...',
];
